import { Injectable } from '@angular/core';
import { EventService } from './event.service';
import { select, Store } from '@ngrx/store';
import { deleteUndefinedEntriesInPlace, copyTitle } from '../helpers/helpers';
import { EntitiesService } from './entities.service';
import { environment } from '../../environments/environment';
import { DeleteResourcesData, RenameResourceData, JoiResource, createBaseResource } from 'globals/store/resources.data';
import { HttpClient } from '@angular/common/http';
import { take } from 'rxjs/operators';
import { Ranking } from 'globals/store/events.data';
import { EntityBase } from 'globals/store/entity.shared.data';
import { createId, jsonCopyObject } from 'globals/helpers/helpers';
import { getOneStorePromise } from '../store/ui.era';
import { lastValueFrom } from 'rxjs';
import { eventsFeature } from '../store/events.era';

// separating this out into its own service for better separation of concerns / clarity between services
@Injectable({ providedIn: 'root' })
export class ResourceService {
  readonly api = `${environment.functionsURL}api/app`;

  constructor(
    private eventService: EventService,
    public http: HttpClient,
    public store: Store,
    private entitiesService: EntitiesService
  ) {}

  async updateTypes(resourceTypes: Ranking[]) {
    resourceTypes.forEach((level, i) => level.rank = resourceTypes.length - i)
    const event = await this.eventService.getCurrentEvent()
    await this.eventService.updateEvent(event.eventId, { resourceTypes })
  }

  async updateEntities(entities: EntityBase[]) {
    await this.updateResources(entities as JoiResource[])
  }
  
  addResourceBase = (partialResource:Partial<JoiResource>): JoiResource => {
    const baseResource = createBaseResource(() => createId()) // [ ] test
    // console.log({ partialResource, baseResource: jsonCopyObject(baseResource) })
    const newResource = { ...baseResource, ...partialResource }
    // console.log({ newResource: jsonCopyObject(newResource) })
    return newResource
  }

  async addResource(resource: JoiResource): Promise<JoiResource> {
    const newResource = this.addResourceBase(resource)
    await this.eventService.saveEventPageEntities('resources', [newResource])
    return newResource
  }

  async addMultipleResources(newResources: Partial<JoiResource[]>): Promise<JoiResource[]> {
    const resources = newResources.map(this.addResourceBase)
    await this.eventService.saveEventPageEntities('resources', resources)
    return resources
  }

  async updateResources(updatedResources: JoiResource[]) {
    updatedResources.forEach(deleteUndefinedEntriesInPlace)
    await this.eventService.saveEventPageEntities('resources', updatedResources)
  }

  async updateResource(resource: JoiResource) {
    await this.updateResources([resource])
  }

  async updateResourceName(eventId: string, resourceId: string, name: string) {
    const url = `${this.api}/rename-resource`
    try {
      const data: RenameResourceData = { eventId, resourceId, name }
      const result = await this.http.post(url, data).pipe(take(1)).toPromise()
      console.log({ result })
      return result
    } catch (response) {
      console.log(response)
      return null
    }
  }

  async deleteResources(resources: JoiResource[], accountEventId: string = null) {
    const url = `${this.api}/delete-resources`
    const event = !accountEventId ? await this.eventService.getCurrentEvent() :
    await lastValueFrom(this.store.select(eventsFeature.selectEntities))[accountEventId]

    try {
      const data: DeleteResourcesData = { eventId: event.eventId, resourceIds: resources.map(r => r.id) }
      const result = await this.http.post(url, data).pipe(take(1)).toPromise()
      console.log({ result })
      return result
    } catch (response) {
      console.log(response)
      return null
    }
  }

  async deleteResource(resource: JoiResource) {
    return await this.deleteResources([resource])
  }

  async templateResources(resources: JoiResource[]) {
    const resourceTemplates = await this.entitiesService.createEntityTemplates('resource', resources)
    const updatedResources = resources.map((r, i) => {
      const { dateAdded, dateModified, templateId, versionNumber } = resourceTemplates[i]
      const updatedResource: JoiResource = { ...r, dateAdded, dateModified, templateId, versionNumber }
      return updatedResource
    })
    await this.updateResources(updatedResources)
  }

  async duplicateResources(resources: JoiResource[]) {    
    const duplicatedResources = resources.map(resource => {
      deleteUndefinedEntriesInPlace(resource)
      const newResource = jsonCopyObject(resource)
      newResource.name = copyTitle(newResource.name, Object.values(resources).map(e => e.name))
      const id = createId()
      delete newResource.confirmed
      newResource.id = id
      newResource.quoteIds = []
      return newResource
    })
    await this.eventService.saveEventPageEntities('resources', duplicatedResources)
  }

  async duplicateResource(resource: JoiResource) {
    await this.duplicateResources([resource])
  }

  changeShowEditLinkInBrief(resource: JoiResource, includeEditUrl: boolean) {
    resource = jsonCopyObject(resource)
    resource.showEditProfileLinkInBrief = includeEditUrl
    console.log({ resource })
    this.updateResource(resource)
  }

}